import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';

// Worker setup with fallback
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.mjs',
    import.meta.url
  ).toString();
} catch (e) {
  // Fallback: disable worker if URL construction fails (some mobile browsers)
  pdfjsLib.GlobalWorkerOptions.workerSrc = '';
}

const navButtonStyle = {
  background: '#007bff',
  border: 'none',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  whiteSpace: 'nowrap',
};

function PDFPage({ pdf, pageNum }) {
  const canvasRef = useRef();
  const renderTaskRef = useRef(null);
  const isMountedRef = useRef(true);
  const containerRef = useRef();

  useEffect(() => {
    isMountedRef.current = true;

    const renderPage = async () => {
      if (!pdf || !isMountedRef.current) return;

      try {
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          await new Promise(resolve => setTimeout(resolve, 50));
          renderTaskRef.current = null;
        }

        const page = await pdf.getPage(pageNum);
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container || !isMountedRef.current) return;

        // Calculate scale based on container width, not hardcoded
        const defaultViewport = page.getViewport({ scale: 1 });
        const containerWidth = container.clientWidth || window.innerWidth * 0.9;
        // Use device pixel ratio but cap it to avoid huge canvases on mobile
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const scale = (containerWidth / defaultViewport.width) * dpr;

        // Safety: cap canvas size to 4096px to avoid mobile GPU limits
        const maxDim = 4096;
        const viewport = page.getViewport({ scale });
        let finalScale = scale;
        if (viewport.width > maxDim || viewport.height > maxDim) {
          const ratio = maxDim / Math.max(viewport.width, viewport.height);
          finalScale = scale * ratio;
        }

        const finalViewport = page.getViewport({ scale: finalScale });
        const context = canvas.getContext('2d');

        canvas.width = finalViewport.width;
        canvas.height = finalViewport.height;

        renderTaskRef.current = page.render({
          canvasContext: context,
          viewport: finalViewport
        });

        await renderTaskRef.current.promise;

        if (isMountedRef.current) {
          renderTaskRef.current = null;
        }
      } catch (error) {
        if (error.name !== 'RenderingCancelledException') {
          console.error("Error rendering page:", error);
        }
        renderTaskRef.current = null;
      }
    };

    // Small delay to ensure container is laid out
    const timer = setTimeout(renderPage, 80);

    const handleResize = () => {
      clearTimeout(timer);
      setTimeout(renderPage, 150);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => setTimeout(handleResize, 200));

    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdf, pageNum]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        marginBottom: '1.5rem',
        maxHeight: 'calc(100vh - 300px)',
        padding: '0.5rem',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          maxWidth: '100%',
          height: 'auto',
          background: '#fff',
          display: 'block',
          pointerEvents: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          borderRadius: '4px',
        }}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />
    </div>
  );
}

export default function PDFPageViewer({ pdfUrl, loadingText = 'Loading document...' }) {
  const [pdf, setPdf] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPDF = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const loadOptions = {
        url: pdfUrl,
        disableAutoFetch: false,
        disableStream: false,
        rangeChunkSize: 65536,
        withCredentials: false,
      };

      // If worker failed to load, use no-worker mode
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        loadOptions.disableWorker = true;
      }

      const loadingTask = pdfjsLib.getDocument(loadOptions);
      const loadedPdf = await loadingTask.promise;
      setPdf(loadedPdf);
      setNumPages(loadedPdf.numPages);
    } catch (err) {
      console.error("Error loading PDF:", err);
      setError(err.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [pdfUrl]);

  useEffect(() => {
    loadPDF();
  }, [loadPDF]);

  return (
    <div
      className="pdf-viewer"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
      style={{
        border: "1px solid #444",
        padding: "1rem",
        borderRadius: "8px",
        background: "#1a1a1a",
        color: "#fff",
        textAlign: "center",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      {loading && <p>{loadingText}</p>}

      {!loading && error && (
        <div style={{ padding: '20px' }}>
          <p style={{ color: '#ff6b6b', marginBottom: '12px' }}>Unable to load document</p>
          <button
            onClick={() => { setPdf(null); loadPDF(); }}
            style={{ ...navButtonStyle, background: '#ff6b6b' }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && pdf && (
        <>
          <PDFPage pdf={pdf} pageNum={pageNum} />

          <div style={{
            marginTop: "1rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap"
          }}>
            <button
              onClick={() => setPageNum((p) => Math.max(p - 1, 1))}
              disabled={pageNum <= 1}
              style={navButtonStyle}
            >
              ← Previous
            </button>

            <span style={{ margin: "0 10px", fontWeight: "bold", minWidth: "120px" }}>
              Page {pageNum} of {numPages}
            </span>

            <button
              onClick={() => setPageNum((p) => Math.min(p + 1, numPages))}
              disabled={pageNum >= numPages}
              style={navButtonStyle}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
